import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Pencil, Loader2, Check, X, Users, BookOpen } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { writeSelectedRecord } from '@/lib/selectedRecord';

const FIELDS = [
  { key: 'name',               label: 'Bezeichnung',      colSpan: 2, required: true },
  { key: 'search_term',        label: 'Suchbegriff',      colSpan: 1 },
  { key: 'short_code',         label: 'Kurzform',         colSpan: 1 },
  { key: 'contact_name',       label: 'Name (Ansprechpartner)', colSpan: 2 },
  { key: 'address',            label: 'Adresse',          colSpan: 2 },
  { key: 'postal_code',        label: 'PLZ',              colSpan: 1 },
  { key: 'city',               label: 'Stadt',            colSpan: 1 },
  { key: 'phone',              label: 'Telefon',          colSpan: 1 },
  { key: 'email',              label: 'E-Mail',           colSpan: 1 },
  { key: 'district',           label: 'Bezirk',           colSpan: 1 },
  { key: 'school_number',      label: 'Schul-Nr.',        colSpan: 1 },
  { key: 'municipality_code',  label: 'Gkz (Gemeindekennziffer)', colSpan: 1 },
  { key: 'region',             label: 'Areal',            colSpan: 1 },
  { key: 'first_contact_date', label: '1. Kontakt',       colSpan: 1, type: 'date' },
];

export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null); // { course_count, teachers: [] }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/locations/${id}`);
      setLocation(r.data);
      setForm(r.data);
      // also fetch derived usage in parallel (gracefully ignore on error)
      api.get(`/locations/${id}/usage`).then(u => setUsage(u.data)).catch(() => {});
    } catch (e) {
      toast.error('Standort nicht gefunden');
      navigate('/locations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (location?.id) writeSelectedRecord({ locationId: location.id });
  }, [location?.id]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // empty strings → null, booleans pass through
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.rooms;
      const r = await api.put(`/locations/${id}`, payload);
      setLocation(r.data);
      setForm(r.data);
      toast.success('Standort gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async () => {
    if (!window.confirm(`Standort „${location?.name}" wirklich löschen?\n\nAlle ${location?.rooms?.length || 0} Räume werden mitgelöscht.`)) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success('Standort gelöscht');
      navigate('/locations');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/locations')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-slate-900 truncate">{location?.name}</h1>
            <p className="text-slate-600 text-sm">
              {location?.short_code && <span className="font-mono mr-2">{location.short_code}</span>}
              · {location?.rooms?.length || 0} Räume
              {usage && <> · {usage.course_count} Kurse · {usage.teachers?.length || 0} Lehrer</>}
            </p>
          </div>
          <Badge className={location?.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {location?.is_active ? 'Aktiv' : 'Inaktiv'}
          </Badge>
          <Button variant="outline" onClick={deleteLocation} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-location-btn">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <Tabs defaultValue="details" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="details" data-testid="tab-details">Gebäude</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="tab-rooms">Räume ({location?.rooms?.length || 0})</TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage">
              <Users className="h-4 w-4 mr-1" />
              Nutzung
            </TabsTrigger>
          </TabsList>

          {/* DETAILS TAB */}
          <TabsContent value="details">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Stammdaten</h2>
                <Button onClick={save} disabled={saving} data-testid="save-location-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Speichern
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELDS.map(f => (
                  <div key={f.key} className={f.colSpan === 2 ? 'md:col-span-2' : ''}>
                    <Label htmlFor={`f-${f.key}`} className="text-xs uppercase tracking-wide text-slate-500">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id={`f-${f.key}`}
                      type={f.type || 'text'}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={`field-${f.key}`}
                      className="mt-1"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <PlzOrtBridge
                    postalCode={form.postal_code}
                    city={form.city}
                    onCity={(value) => setField('city', value)}
                    testId="location-plz"
                  />
                </div>
                <label className="flex items-center gap-2 mt-2 md:col-span-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="h-4 w-4"
                    data-testid="field-is_active"
                  />
                  <span className="text-sm text-slate-700">Standort ist aktiv</span>
                </label>
              </div>
            </div>
          </TabsContent>

          {/* ROOMS TAB */}
          <TabsContent value="rooms">
            <RoomsManager
              locationId={id}
              rooms={location?.rooms || []}
              onChange={load}
            />
          </TabsContent>

          {/* USAGE TAB */}
          <TabsContent value="usage">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Nutzung in Kursen</h2>
              {!usage ? (
                <p className="text-slate-500 italic">Daten werden geladen…</p>
              ) : usage.course_count === 0 ? (
                <p className="text-slate-500 italic">An diesem Standort finden aktuell keine Kurse statt.</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Kurse</div>
                      <div className="text-3xl font-bold text-slate-900 flex items-center gap-2 mt-1">
                        <BookOpen className="h-7 w-7 text-brand-red" />
                        {usage.course_count}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Lehrer</div>
                      <div className="text-3xl font-bold text-slate-900 flex items-center gap-2 mt-1">
                        <Users className="h-7 w-7 text-emerald-600" />
                        {usage.teachers?.length || 0}
                      </div>
                    </div>
                  </div>
                  {usage.teachers?.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-slate-700 mb-2">Lehrer an diesem Standort:</div>
                      <div className="flex flex-wrap gap-2">
                        {usage.teachers.map(t => (
                          <span key={t.id} className="px-3 py-1 rounded-full bg-brand-red/5 text-brand-red text-sm">
                            {t.name} <span className="text-brand-red/70">· {t.course_count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}


// ---------------- Inline Rooms Manager ----------------
function RoomsManager({ locationId, rooms, onChange }) {
  const [editing, setEditing] = useState(null); // {id?, name, description, capacity, wheelchair_accessible}

  const startNew = () => setEditing({ name: '', description: '', capacity: '', wheelchair_accessible: false, is_active: true });
  const startEdit = (r) => setEditing({ ...r, capacity: r.capacity ?? '' });
  const cancel = () => setEditing(null);

  const saveRoom = async () => {
    if (!editing.name.trim()) return toast.error('Bezeichnung erforderlich');
    const payload = {
      name: editing.name.trim(),
      description: editing.description || null,
      capacity: editing.capacity === '' ? null : Number(editing.capacity),
      wheelchair_accessible: !!editing.wheelchair_accessible,
      is_active: editing.is_active !== false,
      location_id: locationId,
    };
    try {
      if (editing.id) {
        await api.put(`/locations/rooms/${editing.id}`, payload);
        toast.success('Raum aktualisiert');
      } else {
        await api.post(`/locations/${locationId}/rooms`, payload);
        toast.success('Raum angelegt');
      }
      setEditing(null);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const removeRoom = async (room) => {
    if (!window.confirm(`Raum „${room.name}" löschen?`)) return;
    try {
      await api.delete(`/locations/rooms/${room.id}`);
      toast.success('Raum gelöscht');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Räume</h2>
        <Button onClick={startNew} disabled={!!editing} data-testid="add-room-btn">
          <Plus className="h-4 w-4 mr-2" /> Neuer Raum
        </Button>
      </div>

      <div className="overflow-hidden border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3 font-medium">Bezeichnung</th>
              <th className="text-left p-3 font-medium">Beschreibung</th>
              <th className="text-center p-3 font-medium w-28">Kapazität</th>
              <th className="text-center p-3 font-medium w-28">Barrierefrei</th>
              <th className="text-right p-3 font-medium w-28">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rooms.length === 0 && !editing && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500 italic">
                Noch keine Räume angelegt
              </td></tr>
            )}
            {[...rooms].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de', { numeric: true, sensitivity: 'base' })).map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`room-row-${r.id}`}>
                <td className="p-3 font-medium text-slate-800">{r.name}</td>
                <td className="p-3 text-slate-600">{r.description || <span className="text-slate-300">—</span>}</td>
                <td className="p-3 text-center text-slate-600">{r.capacity ?? <span className="text-slate-300">—</span>}</td>
                <td className="p-3 text-center">{r.wheelchair_accessible ? <Check className="h-4 w-4 text-emerald-600 inline" /> : <span className="text-slate-300">—</span>}</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(r)} className="p-1 text-slate-500 hover:text-slate-800" title="Bearbeiten" data-testid={`edit-room-${r.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => removeRoom(r)} className="p-1 text-red-500 hover:text-red-700 ml-1" title="Löschen" data-testid={`delete-room-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {editing && (
              <tr className="bg-brand-red/5 border-t border-brand-red/20">
                <td className="p-2"><Input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} placeholder="z.B. Studio 1" autoFocus data-testid="room-name-input" /></td>
                <td className="p-2"><Input value={editing.description || ''} onChange={e => setEditing({...editing, description: e.target.value})} placeholder="z.B. Klavier, 1. OG" data-testid="room-desc-input" /></td>
                <td className="p-2"><Input type="number" min="0" value={editing.capacity} onChange={e => setEditing({...editing, capacity: e.target.value})} className="text-center" /></td>
                <td className="p-2 text-center"><input type="checkbox" checked={!!editing.wheelchair_accessible} onChange={e => setEditing({...editing, wheelchair_accessible: e.target.checked})} className="h-4 w-4" /></td>
                <td className="p-2 text-right">
                  <button onClick={saveRoom} className="p-1 text-emerald-600 hover:text-emerald-800" title="Speichern" data-testid="save-room-btn">
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={cancel} className="p-1 text-slate-500 hover:text-slate-800 ml-1" title="Abbrechen">
                    <X className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
